import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const NONE = "__none__";
const empty = (defaults) => ({
  type: "TASK", title: "", description: "", acceptance_criteria: "", priority: "MEDIUM", assignee_id: "",
  labels: "", story_points: "", estimate_hours: "", due_date: "", parent_id: "", sprint_id: "", ...defaults,
});

export default function WorkItemDialog({ open, onOpenChange, summary, sprints = [], defaults = {}, onCreated }) {
  const { t } = useLang();
  const [form, setForm] = useState(empty(defaults));
  const [parents, setParents] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pid = summary?.project?.id;

  useEffect(() => {
    if (!open) return;
    setForm(empty(defaults));
    if (pid) api.get(`/projects/${pid}/work-items`, { params: { include_done: false } }).then((r) => setParents(r.data.filter((i) => i.type !== "SUBTASK"))).catch(() => {});
  }, [open, pid]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        type: form.type, title: form.title, description: form.description || null,
        acceptance_criteria: form.acceptance_criteria || null, priority: form.priority,
        assignee_id: form.assignee_id || null, labels: form.labels.split(",").map((s) => s.trim()).filter(Boolean),
        story_points: form.story_points === "" ? null : Number(form.story_points),
        estimate_hours: form.estimate_hours === "" ? null : Number(form.estimate_hours),
        due_date: form.due_date || null, parent_id: form.parent_id || null, sprint_id: form.sprint_id || null,
      };
      const { data } = await api.post(`/projects/${pid}/work-items`, payload);
      toast.success(`${t("itemCreated")} · ${data.key}`);
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!summary) return null;
  const parentOptions = parents.filter((p) => ({ EPIC: [], STORY: ["EPIC"], TASK: ["EPIC", "STORY"], BUG: ["EPIC", "STORY"], CHANGE: ["EPIC"], ACTION: ["EPIC", "STORY", "TASK", "BUG", "CHANGE"], SUBTASK: ["STORY", "TASK", "BUG", "CHANGE"] }[form.type] || []).includes(p.type));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="work-item-dialog">
        <DialogHeader><DialogTitle className="font-heading">{t("createWorkItem")} · {summary.project.key}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label>{t("type")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v, parent_id: "" }))}>
                <SelectTrigger data-testid="wi-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{summary.types.map((x) => <SelectItem key={x} value={x} data-testid={`wi-type-option-${x}`}>{t(x)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1 col-span-2"><Label>{t("title")}</Label>
              <Input data-testid="wi-title-input" value={form.title} onChange={set("title")} autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && form.title.trim()) submit(); }} /></div>
          </div>
          <div className="space-y-1"><Label>{t("description")}</Label>
            <Textarea data-testid="wi-description-input" rows={3} value={form.description} onChange={set("description")} /></div>
          <div className="space-y-1"><Label>{t("acceptanceCriteria")}</Label>
            <Textarea data-testid="wi-ac-input" rows={2} value={form.acceptance_criteria} onChange={set("acceptance_criteria")} placeholder="- Given … when … then …" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("priority")}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="wi-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{summary.priorities.map((x) => <SelectItem key={x} value={x}>{t(x)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>{t("assignee")}</Label>
              <Select value={form.assignee_id || NONE} onValueChange={(v) => setForm((f) => ({ ...f, assignee_id: v === NONE ? "" : v }))}>
                <SelectTrigger data-testid="wi-assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t("unassigned")}</SelectItem>
                  {summary.members.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`wi-assignee-option-${m.email?.split("@")[0]}`}>{m.display_name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            {form.type !== "EPIC" && (
              <div className="space-y-1"><Label>{t("parent")}</Label>
                <Select value={form.parent_id || NONE} onValueChange={(v) => setForm((f) => ({ ...f, parent_id: v === NONE ? "" : v }))}>
                  <SelectTrigger data-testid="wi-parent-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t("noParent")}</SelectItem>
                    {parentOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.key} · {p.title.slice(0, 40)}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            {summary.project.methodology !== "KANBAN" && form.type !== "SUBTASK" && (
              <div className="space-y-1"><Label>{t("sprint")}</Label>
                <Select value={form.sprint_id || NONE} onValueChange={(v) => setForm((f) => ({ ...f, sprint_id: v === NONE ? "" : v }))}>
                  <SelectTrigger data-testid="wi-sprint-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t("noSprint")}</SelectItem>
                    {sprints.filter((s) => s.status !== "COMPLETED").map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            <div className="space-y-1"><Label>{t("storyPointsLabel")}</Label>
              <Input data-testid="wi-points-input" type="number" min={0} step={0.5} value={form.story_points} onChange={set("story_points")} /></div>
            <div className="space-y-1"><Label>{t("estimateHours")}</Label>
              <Input data-testid="wi-estimate-input" type="number" min={0} step={0.5} value={form.estimate_hours} onChange={set("estimate_hours")} /></div>
            <div className="space-y-1"><Label>{t("dueDate")}</Label>
              <Input data-testid="wi-due-input" type="date" value={form.due_date} onChange={set("due_date")} /></div>
            <div className="space-y-1"><Label>{t("labels")} <span className="text-muted-foreground">({t("labelsHint")})</span></Label>
              <Input data-testid="wi-labels-input" value={form.labels} onChange={set("labels")} placeholder="auth, api" /></div>
          </div>
          <Button data-testid="wi-create-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={busy || !form.title.trim()} onClick={submit}>
            {busy ? "..." : t("create")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
